import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createKyc,
  getAccessToken,
  getKycStatus,
  type KycVerification,
} from "../lib/kycApi";
import { Toast } from "../components/Toast";
import "./KycPage.css";

const TOKEN_TTL_SECONDS = 1440;

interface LogEntry {
  time: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
}

declare global {
  interface Window {
    snsWebSdk: any;
  }
}

export default function KycPage() {
  const navigate = useNavigate();
  const { levelName } = useParams<{ levelName: string }>();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [status, setStatus] = useState<{ text: string; className: string }>({
    text: "⏸️ Aguardando inicialização",
    className: "idle",
  });
  const [isInitializing, setIsInitializing] = useState(false);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [toast, setToast] = useState<
    { message: string; type?: "error" | "success" | "info" } | null
  >(null);
  const [kycData, setKycData] = useState<KycVerification | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const logsEndRef = useRef<HTMLDivElement>(null);
  const sdkInstanceRef = useRef<any>(null);

  useEffect(() => {
    // Validar se levelName foi fornecido
    if (!levelName) {
      setToast({
        message: "levelName não foi fornecido na URL. Use /kyc/{levelName}",
        type: "error",
      });
      updateStatus("❌ Erro: levelName não fornecido", "error");
      setIsLoadingStatus(false);
      return;
    }

    // Carregar script do Sumsub
    const script = document.createElement("script");
    script.src =
      "https://static.sumsub.com/idensic/static/sns-websdk-builder.js";
    script.async = true;
    script.onload = () => {
      addLog("SDK do Sumsub carregado com sucesso", "success");
    };
    script.onerror = () => {
      addLog("Erro ao carregar SDK do Sumsub", "error");
    };
    document.body.appendChild(script);

    // Carregar status do KYC
    loadKycStatus();

    return () => {
      // Cleanup
      if (sdkInstanceRef.current) {
        try {
          const container = document.getElementById("sumsub-websdk-container");
          if (container) {
            container.innerHTML = "";
          }
          sdkInstanceRef.current = null;
        } catch (error) {
          console.error("Erro ao limpar SDK:", error);
        }
      }
      document.body.removeChild(script);
    };
  }, [levelName]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const addLog = (message: string, type: LogEntry["type"] = "info") => {
    const time = new Date().toLocaleTimeString("pt-BR");
    setLogs((prev) => [...prev, { time, message, type }]);
  };

  const updateStatus = (text: string, className: string) => {
    setStatus({ text, className });
  };

  const loadKycStatus = async () => {
    if (!levelName) return;

    setIsLoadingStatus(true);
    try {
      addLog("Carregando status do KYC...", "info");
      const response = await getKycStatus(levelName);
      setKycData(response.data);
      addLog(`KYC encontrado: ${response.data.id}`, "success");
      addLog(
        `Status: ${response.data.status} | Level: ${response.data.levelName}`,
        "info",
      );
      if (response.data.reviewStatus) {
        addLog(`Review Status: ${response.data.reviewStatus}`, "info");
      }
      if (response.data.reviewAnswer) {
        addLog(`Review Answer: ${response.data.reviewAnswer}`, "info");
      }

      // Atualizar status visual baseado no status do KYC
      updateStatusFromKyc(response.data);
    } catch (error: any) {
      addLog(
        "Nenhum KYC encontrado. Um novo será criado ao inicializar.",
        "warning",
      );
      setKycData(null);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const updateStatusFromKyc = (kyc: KycVerification) => {
    const statusMap: Record<string, { text: string; className: string }> = {
      INIT: { text: "🔄 KYC Iniciado - Aguardando ação", className: "loading" },
      PENDING: { text: "⏳ KYC Pendente", className: "loading" },
      ON_HOLD: { text: "⏸️ KYC em Espera", className: "idle" },
      AWAITING_USER: {
        text: "👤 Aguardando ação do usuário",
        className: "warning",
      },
      AWAITING_SERVICE: {
        text: "🔄 Aguardando processamento",
        className: "loading",
      },
      APPROVED: { text: "✅ KYC Aprovado!", className: "ready" },
      REJECTED: { text: "❌ KYC Rejeitado", className: "error" },
      COMPLETED: { text: "✅ KYC Completo", className: "ready" },
    };

    const statusInfo = statusMap[kyc.status] ||
      { text: `Status: ${kyc.status}`, className: "idle" };
    updateStatus(statusInfo.text, statusInfo.className);
  };

  // Função para abrir SDK quando já existe KYC
  const openKycSDK = async () => {
    if (!kycData) {
      setToast({ message: "Nenhum KYC encontrado", type: "error" });
      return;
    }

    if (!levelName) {
      setToast({ message: "levelName não foi fornecido", type: "error" });
      return;
    }

    setIsInitializing(true);
    updateStatus("⏳ Abrindo verificação KYC...", "loading");

    try {
      addLog(`Abrindo KYC existente: ${kycData.id}`, "info");
      addLog(`Status atual: ${kycData.status}`, "info");

      // Gerar access token (API seleciona automaticamente o primeiro KYC pendente)
      addLog(`Gerando access token (TTL: ${TOKEN_TTL_SECONDS}s)...`, "info");
      const tokenResponse = await getAccessToken(levelName, TOKEN_TTL_SECONDS);
      const token = tokenResponse.data.token;
      addLog(`Access token gerado: ${token.substring(0, 20)}...`, "success");
      addLog(`Applicant ID: ${tokenResponse.data.applicantId}`, "info");
      addLog(`Expira em: ${tokenResponse.data.expiresIn} segundos`, "info");

      // Inicializar SDK
      await initSDK(token);
    } catch (error: any) {
      addLog(`Erro ao abrir KYC: ${error.message}`, "error");
      setToast({
        message: error.message || "Erro ao abrir verificação KYC",
        type: "error",
      });
      updateStatus("❌ Erro ao abrir verificação", "error");
    } finally {
      setIsInitializing(false);
    }
  };

  // Função para criar novo KYC
  const createNewKyc = async () => {
    if (!levelName) {
      setToast({ message: "levelName não foi fornecido", type: "error" });
      return;
    }

    setIsInitializing(true);
    updateStatus("⏳ Criando nova verificação KYC...", "loading");

    try {
      addLog(
        `Criando novo processo de KYC com level '${levelName}'...`,
        "info",
      );

      try {
        const createResponse = await createKyc(levelName);
        setKycData(createResponse.data);
        addLog(`KYC criado com sucesso: ${createResponse.data.id}`, "success");
        addLog(`Applicant ID: ${createResponse.data.applicantId}`, "info");
        addLog(`Level: ${createResponse.data.levelName}`, "info");
        updateStatusFromKyc(createResponse.data);
      } catch (error: any) {
        // Erro 429 - já existe processo
        if (error.message.includes("já iniciou um processo")) {
          addLog("Processo de KYC já existe. Buscando existente...", "warning");
          const statusResponse = await getKycStatus(levelName);
          setKycData(statusResponse.data);
          updateStatusFromKyc(statusResponse.data);
          addLog(`KYC encontrado: ${statusResponse.data.id}`, "success");
        } else {
          throw error;
        }
      }

      // Gerar access token
      addLog(`Gerando access token (TTL: ${TOKEN_TTL_SECONDS}s)...`, "info");
      const tokenResponse = await getAccessToken(levelName, TOKEN_TTL_SECONDS);
      const token = tokenResponse.data.token;
      addLog(`Access token gerado: ${token.substring(0, 20)}...`, "success");
      addLog(`Applicant ID: ${tokenResponse.data.applicantId}`, "info");
      addLog(`Expira em: ${tokenResponse.data.expiresIn} segundos`, "info");

      // Inicializar SDK
      await initSDK(token);
    } catch (error: any) {
      addLog(`Erro ao criar KYC: ${error.message}`, "error");
      setToast({
        message: error.message || "Erro ao criar verificação KYC",
        type: "error",
      });
      updateStatus("❌ Erro ao criar verificação", "error");
    } finally {
      setIsInitializing(false);
    }
  };

  const initSDK = async (token: string) => {
    const container = document.getElementById("sumsub-websdk-container");
    if (!container) {
      throw new Error("Container do SDK não encontrado");
    }

    // Limpar container anterior
    container.innerHTML = "";

    updateStatus("⏳ Carregando SDK...", "loading");
    addLog("Inicializando Sumsub WebSDK...", "info");

    try {
      if (typeof window.snsWebSdk === "undefined") {
        throw new Error(
          "SDK do Sumsub não carregado. Verifique sua conexão com a internet.",
        );
      }

      sdkInstanceRef.current = window.snsWebSdk
        .init(
          token,
          async () => {
            if (!levelName) {
              throw new Error("levelName não foi fornecido");
            }

            addLog(
              `Token expirado. Solicitando novo token (TTL: ${TOKEN_TTL_SECONDS}s)...`,
              "warning",
            );
            try {
              const tokenResponse = await getAccessToken(
                levelName,
                TOKEN_TTL_SECONDS,
              );
              addLog("Novo token gerado com sucesso", "success");
              addLog(
                `Expira em: ${tokenResponse.data.expiresIn} segundos`,
                "info",
              );
              return tokenResponse.data.token;
            } catch (error: any) {
              addLog(`Erro ao renovar token: ${error.message}`, "error");
              throw error;
            }
          },
        )
        .withConf({
          lang: "pt-BR",
        })
        .withOptions({
          addViewportTag: false,
          adaptIframeHeight: true,
        })
        .on("idCheck.onStepCompleted", (payload: any) => {
          addLog(`Etapa concluída: ${JSON.stringify(payload)}`, "success");
          console.log("idCheck.onStepCompleted:", payload);
        })
        .on("idCheck.onSubmitted", (payload: any) => {
          addLog(`Verificação submetida com sucesso!`, "success");
          console.log("idCheck.onSubmitted:", payload);
          updateStatus("✅ Verificação submetida com sucesso!", "ready");
        })
        .on("idCheck.onError", (error: any) => {
          addLog(`Erro no SDK: ${JSON.stringify(error)}`, "error");
          console.error("idCheck.onError:", error);
          updateStatus("❌ Erro no SDK", "error");
        })
        .on("idCheck.onApplicantApproved", (payload: any) => {
          addLog("Aplicante aprovado!", "success");
          console.log("idCheck.onApplicantApproved:", payload);
          updateStatus("✅ Aprovado!", "ready");
        })
        .on("idCheck.onApplicantRejected", (payload: any) => {
          addLog("Aplicante rejeitado", "warning");
          console.log("idCheck.onApplicantRejected:", payload);
          updateStatus("⚠️ Rejeitado", "warning");
        })
        .on("idCheck.onReady", (payload: any) => {
          addLog("SDK pronto para uso", "success");
          console.log("idCheck.onReady:", payload);
          updateStatus("✅ SDK carregado e pronto", "ready");
          setSdkInitialized(true);
        })
        .on("idCheck.onClose", () => {
          addLog("SDK fechado pelo usuário", "info");
          console.log("idCheck.onClose");
          updateStatus("⏸️ SDK fechado", "idle");
        })
        .build();

      sdkInstanceRef.current.launch("#sumsub-websdk-container");
      addLog("SDK inicializado com sucesso!", "success");
    } catch (error: any) {
      addLog(`Erro ao inicializar SDK: ${error.message}`, "error");
      console.error("Erro:", error);
      updateStatus("❌ Erro ao inicializar", "error");
      throw error;
    }
  };

  const resetSDK = () => {
    if (sdkInstanceRef.current) {
      try {
        const container = document.getElementById("sumsub-websdk-container");
        if (container) {
          container.innerHTML = "";
        }
        sdkInstanceRef.current = null;
        setSdkInitialized(false);
        setLogs([]);
        updateStatus("⏸️ Aguardando inicialização", "idle");
      } catch (error: any) {
        setLogs([]);
        addLog(`Erro ao resetar: ${error.message}`, "error");
      }
    }
  };

  const getLogIcon = (type: LogEntry["type"]) => {
    const icons = {
      success: "✅",
      error: "❌",
      warning: "⚠️",
      info: "ℹ️",
    };
    return icons[type];
  };

  return (
    <div className="kyc-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="kyc-container">
        <div className="kyc-header">
          <div className="kyc-header-content">
            <h1>🔐 Verificação KYC</h1>
            <p>Complete seu processo de verificação de identidade</p>
          </div>
          <button
            className="back-to-checklist-button"
            onClick={() => navigate("/checklist")}
          >
            ← Voltar ao Checklist
          </button>
        </div>

        <div className="kyc-content">
          <div className="kyc-controls">
            {/* Status Atual do KYC */}
            {isLoadingStatus
              ? (
                <div className="kyc-status-card loading">
                  <h3>Carregando status...</h3>
                </div>
              )
              : kycData
              ? (
                <div className="kyc-status-card">
                  <h3>Status do KYC</h3>
                  <div className="kyc-status-details">
                    <div className="kyc-status-item">
                      <span className="label">ID:</span>
                      <span className="value">{kycData.id}</span>
                    </div>
                    <div className="kyc-status-item">
                      <span className="label">Status:</span>
                      <span
                        className={`value badge badge-${kycData.status.toLowerCase()}`}
                      >
                        {kycData.status}
                      </span>
                    </div>
                    <div className="kyc-status-item">
                      <span className="label">Level:</span>
                      <span className="value">{kycData.levelName}</span>
                    </div>
                    {kycData.reviewStatus && (
                      <div className="kyc-status-item">
                        <span className="label">Review Status:</span>
                        <span className="value">{kycData.reviewStatus}</span>
                      </div>
                    )}
                    {kycData.reviewAnswer && (
                      <div className="kyc-status-item">
                        <span className="label">Review Answer:</span>
                        <span
                          className={`value badge badge-${kycData.reviewAnswer.toLowerCase()}`}
                        >
                          {kycData.reviewAnswer}
                        </span>
                      </div>
                    )}
                    <div className="kyc-status-item">
                      <span className="label">Applicant ID:</span>
                      <span className="value small">{kycData.applicantId}</span>
                    </div>
                    {kycData.sandboxMode !== undefined && (
                      <div className="kyc-status-item">
                        <span className="label">Modo:</span>
                        <span className="value">
                          {kycData.sandboxMode ? "🧪 Sandbox" : "🚀 Produção"}
                        </span>
                      </div>
                    )}
                    <div className="kyc-status-item">
                      <span className="label">Criado em:</span>
                      <span className="value small">
                        {new Date(kycData.createdAt).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    <div className="kyc-status-item">
                      <span className="label">Atualizado em:</span>
                      <span className="value small">
                        {new Date(kycData.updatedAt).toLocaleString("pt-BR")}
                      </span>
                    </div>
                  </div>
                </div>
              )
              : (
                <div className="kyc-status-card empty">
                  <h3>📋 Nenhuma Verificação KYC</h3>
                  <p>
                    Você ainda não iniciou o processo de verificação de
                    identidade.
                  </p>
                  <p
                    style={{
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                      color: "#9ca3af",
                    }}
                  >
                    Clique no botão abaixo para criar e iniciar sua verificação.
                  </p>
                </div>
              )}

            {/* Botões condicionais baseado no status do KYC */}
            {kycData
              ? (
                // Já existe KYC - botão para abrir SDK
                <>
                  <button
                    className="kyc-init-button"
                    onClick={openKycSDK}
                    disabled={isInitializing || sdkInitialized}
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    {isInitializing
                      ? "⏳ Carregando..."
                      : "📱 Abrir Verificação KYC"}
                  </button>
                </>
              )
              : (
                // Não existe KYC - botão para criar novo
                <button
                  className="kyc-init-button"
                  onClick={createNewKyc}
                  disabled={isInitializing || sdkInitialized}
                >
                  {isInitializing
                    ? "⏳ Criando..."
                    : "🚀 Criar Nova Verificação"}
                </button>
              )}

            <button
              className="kyc-reset-button"
              onClick={resetSDK}
              disabled={!sdkInitialized}
            >
              🔄 Resetar SDK
            </button>

            <button
              className="kyc-reset-button"
              onClick={loadKycStatus}
              disabled={isLoadingStatus}
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              }}
            >
              {isLoadingStatus ? "⏳ Atualizando..." : "🔄 Atualizar Status"}
            </button>

            <div className={`kyc-status ${status.className}`}>
              {status.text}
            </div>

            <div className="kyc-logs">
              {logs.map((log, index) => (
                <div key={index} className={`log-entry ${log.type}`}>
                  <span className="log-time">[{log.time}]</span>
                  <strong>{getLogIcon(log.type)}</strong> {log.message}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>

          <div className="kyc-sdk-container">
            <div id="sumsub-websdk-container"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
